// ADR 23.9.26v §2 — resuming a session with no server, and catching up with the
// server when it comes back.
//
// Beside `useBootstrap` rather than inside `AuthSessionProvider`, whose body is
// at the 50-line budget: the provider gains one hook call, and the "resumed
// offline" mark is this hook's state, never a new public session field.

import { useCallback, useEffect, useState } from 'react'
import { onlineManager } from '@tanstack/react-query'

import type { AuthSession } from '../auth/session'
import type { SessionIdentity } from '../auth/tokenStore'
import { readSessionSnapshot, saveSessionSnapshot } from './sessionSnapshot'

/**
 * The session an unreachable bootstrap settles as, or `null` for signed out.
 *
 * `identity` is the refreshed response's, when the refresh succeeded and only
 * `features()` was unreachable: it wins over the snapshot, whose flags are then
 * borrowed only if the snapshot is the same user's.
 */
export async function resumedSession(identity: SessionIdentity | null): Promise<AuthSession | null> {
  const snapshot = await readSessionSnapshot()
  if (snapshot === null) return null
  if (identity !== null && identity.userId !== snapshot.userId) return null
  return {
    status: 'signedIn',
    userId: identity?.userId ?? snapshot.userId,
    emailVerified: identity?.emailVerified ?? snapshot.emailVerified,
    flags: snapshot.flags,
  }
}

/**
 * The snapshot writer for a settled session: sign-in, replaced flags, and an
 * identity patched through `SESSION_REFRESHED_EVENT` — an effect keyed on the
 * settled values, never a write inside a `setSession` updater (StrictMode runs
 * updaters twice). Skipped while resumed, or a week of offline launches would
 * keep re-dating a snapshot the server never confirmed.
 */
function useSnapshotWriter(session: AuthSession, resumed: boolean): void {
  const { status, userId, emailVerified, flags } = session
  useEffect(() => {
    if (status !== 'signedIn' || resumed || userId === null || flags === null) return
    saveSessionSnapshot({ userId, emailVerified, flags })
  }, [status, userId, emailVerified, flags, resumed])
}

/**
 * While the session was resumed offline, each transition to online re-reads
 * `/features` — which, being authed, runs the real refresh first. The mark
 * clears when the flags are replaced (not on the identity event, which fires
 * one step before `features()` can still fail) or when the session leaves
 * `signedIn`; an unreachable retry leaves it for the next transition.
 *
 * Returns `markResumed`, which the bootstrap's offline branch calls.
 */
export function useOfflineResume(
  session: AuthSession,
  refreshFlags: () => Promise<boolean>,
): () => void {
  const [resumed, setResumed] = useState(false)
  const signedIn = session.status === 'signedIn'
  const markResumed = useCallback(() => setResumed(true), [])

  useSnapshotWriter(session, resumed)
  useEffect(() => {
    if (!signedIn) setResumed(false)
  }, [signedIn])
  useEffect(() => {
    if (!resumed || !signedIn) return
    return onlineManager.subscribe((online) => {
      if (!online) return
      void refreshFlags().then((replaced) => {
        if (replaced) setResumed(false)
      })
    })
  }, [resumed, signedIn, refreshFlags])

  return markResumed
}
